import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { getConversationBySlug, validateConversationAccess, extractUserIdFromSlug } from '../../utils/conversationUrlHelpers';
import UnifiedMessagingPage from './UnifiedMessagingPage';
import Spinner from '../ui/Spinner';

/**
 * Secure conversation route handler
 * Resolves participant-based URLs to conversation IDs securely
 */
const SecureConversationRoute = () => {
  const { participantSlug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  
  const [conversationId, setConversationId] = useState(null);
  const [participant, setParticipant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadConversation = async () => {
      if (!user) {
        navigate('/login-register?redirect=' + encodeURIComponent(window.location.pathname + window.location.search));
        return;
      }

      if (!userProfile) {
        setLoading(true);
        return; // Wait for profile to load
      }

      try {
        setLoading(true);
        setError(null);

        if (!participantSlug) {
          throw new Error('Invalid conversation route - missing participant information');
        }

        // Get job ID from search params if present
        const jobId = searchParams.get('job');

        console.log('Resolving conversation for participant slug:', participantSlug);
        
        // Extract ID hash from slug first to validate format
        const idHash = extractUserIdFromSlug(participantSlug);
        if (!idHash) {
          throw new Error('Invalid participant slug format - could not extract ID hash');
        }
        
        // Resolve the participant slug to get conversation
        const result = await getConversationBySlug(user.id, participantSlug, jobId);

        if (!result.success) {
          throw new Error(result.error || 'Failed to load conversation');
        }

        // Validate access to the conversation
        const accessCheck = await validateConversationAccess(result.conversationId, user.id);
        if (!accessCheck.hasAccess) {
          throw new Error('You do not have access to this conversation');
        }

        setConversationId(result.conversationId);
        setParticipant(result.participant);

        // Update URL to include conversation ID in search params for the messaging component
        const currentParams = new URLSearchParams(searchParams);
        currentParams.set('c', result.conversationId);
        
        // Update URL without causing re-render
        const newUrl = `${window.location.pathname}?${currentParams.toString()}`;
        window.history.replaceState({}, '', newUrl);

      } catch (err) {
        console.error('Error loading secure conversation:', err);
        // Provide more useful error messages based on the error type
        if (err.message.includes('operator does not exist')) {
          setError('Database error: Unable to find user with the provided ID. Please try again later.');
        } else if (err.message.includes('Invalid participant slug')) {
          setError('Invalid conversation link format. Please check the URL and try again.');
        } else if (err.message.includes('User not found')) {
          setError('The user you are trying to contact could not be found.');
        } else {
          setError(err.message || 'Failed to load conversation');
        }
      } finally {
        setLoading(false);
      }
    };

    loadConversation();
  }, [user?.id, userProfile?.id, participantSlug, searchParams, navigate]);

  // Show loading spinner while resolving conversation
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="flex flex-col items-center space-y-4">
          <Spinner size="large" />
          <p className="text-gray-600">Loading conversation...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm border p-8 text-center">
          <div className="mb-4">
            <svg className="mx-auto h-12 w-12 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Conversation Not Found</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          
          <div className="bg-gray-50 p-3 rounded-lg text-left text-sm mb-6">
            <p className="text-gray-700 mb-2"><strong>Possible reasons:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1">
              <li>The user may not exist</li>
              <li>You don't have permission to message this user</li>
              <li>There was a technical issue with the conversation</li>
            </ul>
          </div>
          
          <div className="space-y-3">
            <button
              onClick={() => navigate('/messages')}
              className="w-full bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors"
            >
              Back to Messages
            </button>
            <button
              onClick={() => window.location.reload()}
              className="w-full text-gray-600 hover:text-gray-800 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render the unified messaging page with the resolved conversation
  return (
    <UnifiedMessagingPage 
      preloadedConversationId={conversationId}
      preloadedParticipant={participant}
    />
  );
};

export default SecureConversationRoute;